import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import Text from '@cartwhl/ui/base/Text';
import CircularProgressIndicator from '@cartwhl/ui/messaging/CircularProgressIndicator';

import { motionEditorVideoExportState } from '../videoExport/motionEditorVideoExportStore';
import SceneVideoDownloadControls from './SceneVideoDownloadControls';

export default function SceneVideoDownloadPanel() {
  const { recordedVideo, isRecording, videoGenerationProgress } = useSnapshot(
    motionEditorVideoExportState,
  );

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [canvasDimensions, setCanvasDimensions] = useState('—');

  useEffect(() => {
    if (!recordedVideo || recordedVideo.size === 0) {
      setVideoUrl('');
      return;
    }
    const url = URL.createObjectURL(recordedVideo);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [recordedVideo]);

  useEffect(() => {
    const canvas = document.querySelector(
      '#cartwhl-player-canvas canvas',
    ) as HTMLCanvasElement | null;
    if (!canvas) {
      return;
    }

    const update = () => {
      setCanvasDimensions(`${canvas.width}x${canvas.height}`);
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (videoRef.current && videoUrl) {
      videoRef.current.load();
    }
  }, [videoUrl]);

  const renderVideoArea = () => {
    if (videoUrl) {
      return (
        <video
          className="max-h-[340px] max-w-full rounded-lg object-contain 2xl:max-h-[400px]"
          key={videoUrl}
          ref={videoRef}
          muted
          playsInline
          loop
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }

    if (isRecording || videoGenerationProgress) {
      return (
        <div className="flex flex-col items-center justify-center space-y-4 py-8">
          <CircularProgressIndicator
            progress={videoGenerationProgress?.percentage}
            className="mx-auto"
          />
          <Text variant="studio/body/sm" className="text-center">
            {typeof videoGenerationProgress?.percentage === 'number'
              ? `Generating video... ${Math.round(videoGenerationProgress.percentage)}%`
              : 'Preparing video generation...'}
          </Text>
          {videoGenerationProgress?.time && (
            <Text
              variant="studio/body/sm"
              className="text-gray-60 dark:text-gray-30"
            >
              Time: {videoGenerationProgress.time}
            </Text>
          )}
        </div>
      );
    }

    return (
      <div className="bg-gray-15 dark:bg-gray-75 flex aspect-video w-full items-center justify-center rounded-lg px-6 py-10">
        <Text variant="studio/body/sm" className="text-center text-balance">
          Click Download to generate an MP4 from the scene.
        </Text>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="bg-gray-15 dark:bg-gray-75 flex items-center justify-center rounded-lg">
          {renderVideoArea()}
        </div>
        {videoUrl && (
          <div className="pt-4">
            <SceneVideoDownloadControls videoRef={videoRef} videoUrl={videoUrl} />
          </div>
        )}
      </div>

      <div className="border-gray-30 dark:border-gray-75 grid grid-cols-2 gap-x-4 gap-y-6 border-t pt-6 sm:grid-cols-3">
        <Text variant="studio/body/sm">Canvas size:</Text>
        <div className="flex items-center">
          <Text className="text-gray-60" variant="studio/body/sm">
            {canvasDimensions}
          </Text>
        </div>
      </div>
    </div>
  );
}
